import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { motion } from 'framer-motion';

const { FiArrowLeft, FiScale, FiFileText, FiClock, FiAlertTriangle, FiFolder, FiUsers } = FiIcons;

const TribunalPrep = () => {
  const [activeTab, setActiveTab] = useState('preparation');

  const tribunalWorkflow = {
    preparation: {
      title: 'Tribunal Preparation Workflow',
      stages: [
        {
          stage: 'Immediate Actions (Day 1-7)',
          urgency: 'critical',
          icon: FiAlertTriangle,
          actions: [
            '**Acknowledge ET1 claim** within 28 days',
            'Notify insurance company/legal team',
            'Preserve all relevant documents (litigation hold)',
            'Identify key witnesses and secure statements',
            'Review employee file for compliance gaps'
          ],
          compliance: 'Legal: 28-day deadline to respond to ET1 claim'
        },
        {
          stage: 'Case Assessment (Week 1-2)',
          urgency: 'high',
          icon: FiScale,
          actions: [
            'Analyze strengths and weaknesses of claim',
            'Review dismissal/disciplinary process for compliance',
            'Assess potential compensation exposure',
            'Consider early settlement opportunities',
            'Identify procedural defenses'
          ],
          compliance: 'ACAS: Early settlement can reduce costs and risk'
        },
        {
          stage: 'ET3 Response Preparation (Week 2-4)',
          urgency: 'high',
          icon: FiFileText,
          actions: [
            'Draft comprehensive ET3 response',
            'Address each allegation specifically',
            'Raise procedural challenges if applicable',
            'Submit within 28-day deadline',
            'Request case management orders if needed'
          ],
          compliance: 'Legal: ET3 must be submitted within 28 days or face default judgment'
        },
        {
          stage: 'Document Bundle Creation (Week 4-8)',
          urgency: 'medium',
          icon: FiFolder,
          actions: [
            'Compile chronological document bundle',
            'Include: contracts, policies, disciplinary records',
            'Add: investigation notes, witness statements',
            'Organize: correspondence, meeting notes',
            'Paginate and index all documents'
          ],
          compliance: 'Legal: Complete disclosure required, privilege claims must be justified'
        },
        {
          stage: 'Witness Preparation (Week 6-10)',
          urgency: 'medium',
          icon: FiUsers,
          actions: [
            'Identify all relevant witnesses',
            'Prepare witness statements',
            'Brief witnesses on tribunal process',
            'Conduct practice sessions',
            'Ensure witness availability for hearing'
          ],
          compliance: 'ACAS: Witnesses should stick to facts, avoid speculation'
        },
        {
          stage: 'Final Hearing Preparation (Week 10-12)',
          urgency: 'high',
          icon: FiClock,
          actions: [
            'Prepare opening statement',
            'Review cross-examination strategy',
            'Organize hearing bundles',
            'Brief legal representation',
            'Prepare for potential settlement at door of tribunal'
          ],
          compliance: 'Legal: Final opportunity for settlement before public hearing'
        }
      ]
    },
    evidence: {
      title: 'Evidence & Documentation Requirements',
      categories: [
        {
          category: 'Employment Records',
          essential: true,
          items: [
            'Original employment contract and variations',
            'Job description and person specification',
            'Salary and benefits records',
            'Holiday and absence records',
            'Performance reviews and appraisals'
          ]
        },
        {
          category: 'Disciplinary/Grievance Records',
          essential: true,
          items: [
            'All disciplinary correspondence',
            'Investigation notes and reports',
            'Hearing minutes and recordings',
            'Witness statements',
            'Appeal documentation'
          ]
        },
        {
          category: 'Policies & Procedures',
          essential: true,
          items: [
            'Employee handbook current at time',
            'Disciplinary and grievance procedures',
            'Equal opportunities policy',
            'Health and safety policies',
            'Training records'
          ]
        },
        {
          category: 'Communications',
          essential: false,
          items: [
            'Email correspondence',
            'Text messages if relevant',
            'Social media posts',
            'Internal memos',
            'Meeting notes and minutes'
          ]
        }
      ]
    },
    timeline: {
      title: 'Critical Tribunal Timeline',
      milestones: [
        {
          event: 'ET1 Claim Received',
          deadline: 'Day 0',
          action: 'Start 28-day response clock',
          critical: true
        },
        {
          event: 'ACAS Early Conciliation',
          deadline: 'Day 1-14',
          action: 'Engage in settlement discussions',
          critical: false
        },
        {
          event: 'ET3 Response Due',
          deadline: 'Day 28',
          action: 'Submit defense response',
          critical: true
        },
        {
          event: 'Case Management Orders',
          deadline: 'Week 6-10',
          action: 'Comply with tribunal directions',
          critical: true
        },
        {
          event: 'Document Exchange',
          deadline: 'Week 8-12',
          action: 'Exchange witness statements and documents',
          critical: true
        },
        {
          event: 'Final Hearing',
          deadline: 'Month 6-18',
          action: 'Present case at tribunal',
          critical: true
        }
      ]
    }
  };

  const currentContent = tribunalWorkflow[activeTab];

  const getUrgencyColor = (urgency) => {
    switch (urgency) {
      case 'critical': return 'bg-red-600';
      case 'high': return 'bg-orange-600';
      case 'medium': return 'bg-yellow-600';
      default: return 'bg-gray-600';
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="flex items-center mb-6">
        <Link to="/exit-processes" className="mr-4">
          <SafeIcon icon={FiArrowLeft} className="text-2xl text-gray-600 hover:text-gray-900" />
        </Link>
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Employment Tribunal Preparation</h1>
          <p className="text-gray-600">Comprehensive workflow for defending tribunal claims</p>
        </div>
      </div>

      <div className="mb-6">
        {/* Mobile Dropdown */}
        <div className="md:hidden">
          <label htmlFor="tribunal-tabs" className="sr-only">Select a section</label>
          <select
            id="tribunal-tabs"
            className="block w-full rounded-md border-gray-300 focus:border-orange-500 focus:ring-orange-500 py-2 px-3"
            value={activeTab}
            onChange={(e) => setActiveTab(e.target.value)}
          >
            {Object.keys(tribunalWorkflow).map((tab) => (
              <option key={tab} value={tab}>
                {tab === 'evidence' ? 'Evidence & Docs' : tab.charAt(0).toUpperCase() + tab.slice(1)}
              </option>
            ))}
          </select>
        </div>

        {/* Desktop Tabs */}
        <div className="hidden md:flex space-x-4">
          {Object.keys(tribunalWorkflow).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 rounded-lg font-medium transition-colors capitalize ${activeTab === tab
                  ? 'bg-orange-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
            >
              {tab === 'evidence' ? 'Evidence & Docs' : tab}
            </button>
          ))}
        </div>
      </div>

      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">{currentContent.title}</h2>
      </div>

      {activeTab === 'preparation' && (
        <div className="space-y-6">
          {currentContent.stages.map((stage, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-white rounded-lg shadow-lg overflow-hidden"
            >
              <div className={`${getUrgencyColor(stage.urgency)} px-6 py-4`}>
                <div className="flex items-center space-x-3">
                  <SafeIcon icon={stage.icon} className="text-white text-2xl" />
                  <div>
                    <h3 className="text-white font-semibold text-xl">{stage.stage}</h3>
                    <span className="text-white opacity-90 text-sm capitalize">
                      {stage.urgency} Priority
                    </span>
                  </div>
                </div>
              </div>

              <div className="p-6">
                <ul className="space-y-3 mb-4">
                  {stage.actions.map((action, actionIndex) => (
                    <li key={actionIndex} className="flex items-start space-x-2">
                      <div className="w-2 h-2 bg-orange-500 rounded-full mt-2 flex-shrink-0" />
                      <span
                        className="text-gray-700"
                        dangerouslySetInnerHTML={{
                          __html: action.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                        }}
                      />
                    </li>
                  ))}
                </ul>

                <div className="bg-orange-50 border border-orange-200 rounded p-3">
                  <p className="text-sm text-orange-800">
                    <strong>Compliance Note:</strong> {stage.compliance}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {activeTab === 'evidence' && (
        <div className="space-y-6">
          {currentContent.categories.map((category, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-white rounded-lg shadow-lg overflow-hidden"
            >
              <div className={`px-6 py-4 ${category.essential ? 'bg-red-600' : 'bg-blue-600'}`}>
                <div className="flex items-center justify-between">
                  <h3 className="text-white font-semibold text-xl">{category.category}</h3>
                  <span className="bg-white bg-opacity-20 text-white px-3 py-1 rounded text-sm font-medium">
                    {category.essential ? 'Essential' : 'Supporting'}
                  </span>
                </div>
              </div>

              <div className="p-6">
                <ul className="grid md:grid-cols-2 gap-3">
                  {category.items.map((item, itemIndex) => (
                    <li key={itemIndex} className="flex items-center space-x-2">
                      <SafeIcon
                        icon={category.essential ? FiAlertTriangle : FiFileText}
                        className={`${category.essential ? 'text-red-500' : 'text-blue-500'} flex-shrink-0`}
                      />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {activeTab === 'timeline' && (
        <div className="space-y-4">
          {currentContent.milestones.map((milestone, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 }}
              className={`bg-white rounded-lg shadow border-l-4 ${milestone.critical ? 'border-red-500' : 'border-blue-500'
                }`}
            >
              <div className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${milestone.critical ? 'bg-red-100' : 'bg-blue-100'
                      }`}>
                      <SafeIcon
                        icon={milestone.critical ? FiAlertTriangle : FiClock}
                        className={`text-xl ${milestone.critical ? 'text-red-600' : 'text-blue-600'
                          }`}
                      />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 text-lg">{milestone.event}</h3>
                      <p className="text-gray-600">{milestone.action}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className={`font-bold text-lg ${milestone.critical ? 'text-red-600' : 'text-blue-600'
                      }`}>
                      {milestone.deadline}
                    </span>
                    {milestone.critical && (
                      <p className="text-red-500 text-sm font-medium">Critical Deadline</p>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      <div className="mt-8 bg-red-50 border border-red-200 rounded-lg p-6">
        <div className="flex items-start space-x-3">
          <SafeIcon icon={FiAlertTriangle} className="text-red-600 text-xl mt-0.5" />
          <div>
            <h4 className="font-semibold text-red-900 mb-2">Critical Success Factors</h4>
            <div className="text-red-800 space-y-1 text-sm">
              <p><strong>Strict Deadlines:</strong> Missing tribunal deadlines can result in automatic judgment</p>
              <p><strong>Complete Documentation:</strong> Failure to disclose relevant documents can damage credibility</p>
              <p><strong>Consistent Process:</strong> Any deviation from stated procedures will be scrutinized</p>
              <p><strong>Witness Credibility:</strong> Inconsistent testimony can undermine entire defense</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TribunalPrep;